import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { User, Building2, FileText, Mail } from "lucide-react";
import DashboardLayout from "@/components/layouts/DashboardLayout";
import { useQuery } from "@tanstack/react-query";

const Dashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Use React Query to fetch the user profile
  const { data: profile, isLoading } = useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error("No user found");

        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .single();

        if (error) {
          toast({
            title: "Error",
            description: "Failed to fetch profile data",
            variant: "destructive",
          });
          throw error;
        }

        return data;
      } catch (error) {
        console.error("Error fetching profile:", error);
        throw error;
      }
    },
    retry: 1,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Format the last login date if available
  const lastLogin = profile?.last_login
    ? new Date(profile.last_login).toLocaleString()
    : null;

  return (
    <DashboardLayout>
      <Helmet>
        <title>Dashboard - NumberzInsight</title>
        <meta
          name="description"
          content="Access your NumberzInsight dashboard to manage your business services and profile."
        />
      </Helmet>

      <div className="space-y-8">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg"
        >
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
            Hi, {profile?.full_name?.split(" ")[0] || "there"}!
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Manage your business services and profile from your personalized
            dashboard.
          </p>
          {lastLogin && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              Last login: {lastLogin}
            </p>
          )}
        </motion.div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg"
          >
            <User className="h-8 w-8 text-primary mb-4" />
            <h2 className="text-lg font-semibold mb-2">Profile Information</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Update your personal and business information
            </p>
            <Button
              onClick={() => navigate("/dashboard/profile")}
              className="w-full"
            >
              Edit Profile
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg"
          >
            <Building2 className="h-8 w-8 text-primary mb-4" />
            <h2 className="text-lg font-semibold mb-2">Business Details</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              View and manage your business registration details
            </p>
            <Button
              onClick={() => navigate("/dashboard/business")}
              className="w-full"
            >
              View Details
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg"
          >
            <FileText className="h-8 w-8 text-primary mb-4" />
            <h2 className="text-lg font-semibold mb-2">Documents</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Access and manage your important documents
            </p>
            <Button
              onClick={() => navigate("/dashboard/documents")}
              className="w-full"
            >
              View Documents
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg"
          >
            <Mail className="h-8 w-8 text-primary mb-4" />
            <h2 className="text-lg font-semibold mb-2">Email Management</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Manage email templates, preferences, and monitoring
            </p>
            <Button onClick={() => navigate("/admin/email")} className="w-full">
              Email Dashboard
            </Button>
          </motion.div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
